import * as React from 'react';
import {PropsWithChildren, ReactNode} from 'react';
import {styled} from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {Breakpoint, LinearProgress, Slide, SxProps} from '@mui/material';
import {TransitionProps} from '@mui/material/transitions';

export const BootstrapDialog = styled(Dialog)(({theme}) => ({
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const Transition = React.forwardRef(function Transition(
    props: PropsWithChildren<TransitionProps> & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export interface DialogTitleProps {
    children?: React.ReactNode;
    onClose: (() => void) | undefined;
}

export const AppDialogTitle = (props: DialogTitleProps) => {
    const {children, onClose, ...other} = props;

    return (
        <DialogTitle
            sx={{
                m: 0,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
            {...other}
        >
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: theme => theme.palette.grey[500],
                        ml: 2,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
};

type ActionArgs = {
    onClose: () => void;
    loading?: boolean;
};

// Copy type to avoid circular dependency
type StackedModalProps = {
    open: boolean;
    modalIndex?: number;
    modalId?: string;
};

type Props = PropsWithChildren<
    {
        title?: ReactNode;
        actions?: (args: ActionArgs) => React.ReactNode;
        onClose: () => void;
        hideCloseButton?: boolean;
        loading?: boolean;
        maxWidth?: Breakpoint | false;
        fullScreen?: boolean;
        disablePadding?: boolean | undefined;
        sx?: SxProps;
        disableEscapeKeyDown?: boolean;
    } & StackedModalProps
>;

export type {Props as AppDialogProps};

export default function AppDialog({
    title,
    children,
    actions,
    loading,
    onClose,
    hideCloseButton,
    disablePadding,
    fullScreen,
    open = true,
    maxWidth = 'md',
    sx,
    disableEscapeKeyDown,
}: Props) {
    const progressHeight = 3;

    const handleClose = () => {
        onClose();
    };

    return (
        <BootstrapDialog
            TransitionComponent={Transition}
            onClose={handleClose}
            open={open}
            fullWidth={true}
            maxWidth={maxWidth}
            fullScreen={fullScreen}
            sx={sx}
            disableEscapeKeyDown={disableEscapeKeyDown}
        >
            {title && (
                <AppDialogTitle
                    onClose={!hideCloseButton ? handleClose : undefined}
                >
                    {title}
                </AppDialogTitle>
            )}
            <DialogContent
                dividers
                sx={{
                    'p': disablePadding ? 0 : 2,
                    'border': disablePadding ? 0 : undefined,
                    '&:empty': {
                        display: 'none',
                    },
                }}
            >
                {children}
            </DialogContent>
            {loading && (
                <LinearProgress
                    style={{
                        height: progressHeight,
                        marginBottom: -progressHeight,
                    }}
                />
            )}
            {actions && (
                <DialogActions>
                    {actions({
                        onClose: handleClose,
                        loading,
                    })}
                </DialogActions>
            )}
        </BootstrapDialog>
    );
}
